import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { countryMapInfo } from "@/lib/geo";
import { Loader2, MapPin, Check, AlertTriangle, Wand2 } from "lucide-react";

const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();

function bigrams(s) {
  const out = [];
  for (let i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2));
  return out;
}
function dice(a, b) {
  const A = bigrams(a), B = bigrams(b);
  if (!A.length || !B.length) return 0;
  const m = new Map();
  A.forEach((g) => m.set(g, (m.get(g) || 0) + 1));
  let hit = 0;
  B.forEach((g) => { const c = m.get(g) || 0; if (c > 0) { hit++; m.set(g, c - 1); } });
  return (2 * hit) / (A.length + B.length);
}
// Common location aliases (normalized) → canonical names for matching.
const ALIASES = {
  "uk": "united kingdom", "gb": "united kingdom", "britain": "united kingdom",
  "great britain": "united kingdom", "england": "united kingdom",
  "us": "united states", "usa": "united states", "u s": "united states",
  "u s a": "united states", "america": "united states",
  "uae": "united arab emirates", "nz": "new zealand",
  "drc": "democratic republic of the congo", "ivory coast": "cote d ivoire",
  "czechia": "czech republic", "burma": "myanmar", "holland": "netherlands",
  "south korea": "korea", "north korea": "dem rep korea",
};

export function bestMatch(raw, features) {
  const nr0 = norm(raw);
  if (!nr0) return null;
  const candidates = [nr0, ALIASES[nr0]].filter(Boolean);
  let best = null, bestScore = 0;
  for (const f of features) {
    const nf = norm(f);
    for (const nr of candidates) {
      if (nf === nr) return { name: f, kind: nr === nr0 ? "exact" : "auto" };
      let s = dice(nr, nf);
      if (nr.length >= 4 && (nf.includes(nr) || nr.includes(nf))) s = Math.max(s, 0.75);
      else if (nr.length >= 3 && nf.split(" ").includes(nr)) s = Math.max(s, 0.7);
      if (s > bestScore) { bestScore = s; best = f; }
    }
  }
  return best && bestScore >= 0.55 ? { name: best, kind: "auto" } : null;
}

async function loadFeatureNames(mapShape) {
  if ((mapShape || "").startsWith("custom:")) {
    const { data } = await api.get(`/geo-maps/${mapShape.slice(7)}/geojson`, { timeout: 60000 });
    return [...new Set((data.features || []).map((f) => f.properties?.name || f.properties?.NAME).filter(Boolean))];
  }
  const info = mapShape && mapShape !== "world" ? countryMapInfo(mapShape) : { file: "/maps/world.json" };
  if (!info) return [];
  const r = await fetch(`${process.env.PUBLIC_URL || ""}${info.file}`);
  const geo = await r.json();
  return [...new Set((geo.features || []).map((f) => f.properties?.name || f.properties?.NAME).filter(Boolean))];
}

// Territory matching helper: maps raw location values from the dataset onto the
// selected map's feature names (exact → normalized → fuzzy), with manual review.
export function TerritoryMatchDialog({ open, onOpenChange, widget, config, opts, patchOpts }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState([]);
  const [rows, setRows] = useState([]); // [{raw, sel, kind}]
  const dim = (config?.dimensions || [])[0];
  const mapShape = opts?.mapShape || "world";

  useEffect(() => {
    if (!open || !widget?.dataset_id) return;
    let active = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        let field = dim?.field;
        if (!field && dim?.key) {
          const { data: model } = await api.get(`/datasets/${widget.dataset_id}/model`);
          field = (model?.dimensions || []).find((d) => d.key === dim.key)?.field;
        }
        if (!field) throw new Error("This map has no location field yet — add a location dimension first.");
        const [{ data: dv }, feats] = await Promise.all([
          api.post(`/datasets/${widget.dataset_id}/distinct`, { field }),
          loadFeatureNames(mapShape),
        ]);
        if (!active) return;
        if (!feats.length) throw new Error("Could not load the map's region names.");
        const existing = opts?.territoryMap || {};
        const featSet = new Set(feats.map(norm));
        const next = (dv?.values || []).map((v) => {
          const raw = String(v);
          if (existing[raw]) return { raw, sel: existing[raw], kind: "manual" };
          if (featSet.has(norm(raw))) {
            const exact = feats.find((f) => norm(f) === norm(raw));
            return { raw, sel: exact, kind: exact === raw ? "exact" : "auto" };
          }
          const m = bestMatch(raw, feats);
          return m ? { raw, sel: m.name, kind: "auto" } : { raw, sel: "", kind: "unmatched" };
        });
        setFeatures(feats.sort((a, b) => a.localeCompare(b)));
        setRows(next);
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || e.message || "Failed to load values");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stats = useMemo(() => ({
    matched: rows.filter((r) => r.sel).length,
    unmatched: rows.filter((r) => !r.sel).length,
  }), [rows]);

  const save = () => {
    const map = {};
    rows.forEach((r) => { if (r.sel && r.sel !== r.raw) map[r.raw] = r.sel; });
    patchOpts({ territoryMap: map });
    toast.success(`Territory mapping saved (${Object.keys(map).length} remapped, ${stats.unmatched} unmatched)`);
    onOpenChange(false);
  };
  const clearAll = () => {
    patchOpts({ territoryMap: {} });
    toast.success("Territory mapping cleared");
    onOpenChange(false);
  };

  const KIND_BADGE = {
    exact: ["Exact", "bg-[color:rgba(22,163,74,0.12)] text-[#16A34A] border-[color:rgba(22,163,74,0.35)]"],
    auto: ["Auto", "bg-[color:rgba(43,78,255,0.10)] text-[color:var(--brand-ocean)] border-[color:rgba(43,78,255,0.30)]"],
    manual: ["Manual", "bg-[color:rgba(255,107,53,0.12)] text-[#FF6B35] border-[color:rgba(255,107,53,0.35)]"],
    unmatched: ["No match", "bg-[color:rgba(220,38,38,0.10)] text-[#DC2626] border-[color:rgba(220,38,38,0.30)]"],
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="territory-match-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><MapPin className="w-5 h-5 text-[color:var(--brand-ocean)]" />Match territories</DialogTitle>
          <DialogDescription>
            Map your data's location values onto the region names of this map. Exact and close matches are suggested automatically — review and fix the rest.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-[color:var(--danger)]"><AlertTriangle className="w-5 h-5 mx-auto mb-1" />{error}</div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[11px] text-[color:var(--ink-3)]">
              <span data-testid="territory-match-stats" className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#16A34A]" />{stats.matched} of {rows.length} matched</span>
              {stats.unmatched > 0 && <span className="text-[#DC2626]">{stats.unmatched} need review</span>}
              <span className="ml-auto">{features.length} map regions</span>
            </div>
            <div className="rounded-xl border border-[color:var(--glass-border)] overflow-auto max-h-[46vh]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[color:var(--surface-2)] z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-[color:var(--ink-2)]">Your data value</th>
                    <th className="text-left px-3 py-2 font-medium text-[color:var(--ink-2)]">Map region</th>
                    <th className="text-left px-3 py-2 font-medium text-[color:var(--ink-2)] w-20">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const [label, cls] = KIND_BADGE[r.kind] || KIND_BADGE.unmatched;
                    return (
                      <tr key={r.raw} data-testid={`territory-row-${i}`} className="border-t border-[color:var(--glass-border)]">
                        <td className="px-3 py-1.5 text-[color:var(--ink-2)] whitespace-nowrap max-w-[200px] truncate">{r.raw}</td>
                        <td className="px-2 py-1">
                          <Select value={r.sel || "__none__"} onValueChange={(v) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, sel: v === "__none__" ? "" : v, kind: v === "__none__" ? "unmatched" : "manual" } : x))}>
                            <SelectTrigger data-testid={`territory-select-${i}`} className="h-7 text-xs bg-[color:var(--surface)] w-full"><SelectValue placeholder="Pick a region" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— not on this map —</SelectItem>
                              {features.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${cls}`}>{label}</span></td>
                      </tr>
                    );
                  })}
                  {!rows.length && <tr><td colSpan={3} className="px-3 py-6 text-center text-[color:var(--ink-3)]">No location values found.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
        <DialogFooter className="gap-2">
          {Object.keys(opts?.territoryMap || {}).length > 0 && (
            <Button data-testid="territory-clear-btn" variant="outline" size="sm" onClick={clearAll}>Clear mapping</Button>
          )}
          <Button data-testid="territory-save-btn" size="sm" onClick={save} disabled={loading || !!error}>
            <Wand2 className="w-3.5 h-3.5 mr-1" />Save mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
